import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Chip from "@mui/material/Chip";
import { RolesContext } from "../../context/User";
import { useErrValidate } from "../../utilitis/AuthorizationPage";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import logger from "../../utilitis/logger";
import { renderHighlightedPartDes } from "./advanceUtils";
import AdvanceConfirmDialog, { useAdvanceConfirm } from "./AdvanceConfirmDialog";

const NursingHomeOrderFormMobile = () => {
  const [stateroles, dispatchroles] = useContext(RolesContext);
  const { validationPage } = useErrValidate();
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [deliveryDays, setDeliveryDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFreeQuantity, setIsFreeQuantity] = useState(false);
  const [sites, setSites] = useState([]);

  const [formData, setFormData] = useState({
    reference: "",
    dueDate: "",
    dcode: "",
    notes: "",
    driverText: "",
    items: [],
    selectedFiles: [],
  });

  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [quantityErrorDialog, setQuantityErrorDialog] = useState({
    open: false,
    itemIndex: null,
    partname: "",
    enteredQuantity: 0,
    packQuant: 0,
  });

  // Advance product confirmation dialog
  const advanceConfirm = useAdvanceConfirm((itemIndex, partname) =>
    handleItemChange(itemIndex, "partname", partname),
  );

  const contactName = stateroles.name || "";
  const phoneNum = stateroles.phone || "";

  useEffect(() => {
    validationPage("nursinghome");
    fetchProducts();
    fetchDeliveryDays();
    fetchSites();
  }, [stateroles.userId]);

  useEffect(() => {
    if (location.state?.formData) {
      setFormData(location.state.formData);
    }
  }, []);

  const generateDeliveryOptions = () =>
    deliveryDays.map((dateOption) => ({
      value: `${dateOption.date}T00:00:00+02:00`,
      label: dateOption.formatted,
      date: dateOption.date,
    }));

  const CHUNK_SIZE = 1024 * 1024;

  const readFileInChunks = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      const chunks = [];
      let offset = 0;

      const readNextChunk = () => {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        reader.readAsDataURL(chunk);
      };

      reader.onload = (e) => {
        if (e.target.result) {
          chunks.push(e.target.result.split(",")[1]);
          offset += CHUNK_SIZE;
          if (offset < file.size) {
            readNextChunk();
          } else {
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              chunks,
              totalChunks: chunks.length,
            });
          }
        }
      };

      reader.onerror = (err) => reject(err);
      readNextChunk();
    });

  const handleFileChange = async (event) => {
    try {
      setUploadingFiles(true);
      setError(null);
      const files = Array.from(event.target.files);
      if (!files.length) return;
      const fileDataArray = await Promise.all(files.map(readFileInChunks));
      setFormData((prev) => ({
        ...prev,
        selectedFiles: [...prev.selectedFiles, ...fileDataArray],
      }));
    } catch (err) {
      setError("שגיאה בקריאת הקבצים. נסה שוב.");
    } finally {
      setUploadingFiles(false);
      event.target.value = null;
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== index),
    }));
  };

  const handleExtractFromPdf = async (fileIndex, directFileData = null) => {
    try {
      setUploadingFiles(true);
      setError(null);
      const file = directFileData || formData.selectedFiles[fileIndex];
      if (!file || file.type !== "application/pdf") {
        setError("ניתן לחלץ נתונים רק מקבצי PDF");
        return;
      }
      const fullContent = Array.isArray(file.chunks)
        ? file.chunks.join("")
        : file.chunks;
      const response = await axios.post(
        "/nursinghome/extract-pdf",
        { pdfContent: fullContent },
        { withCredentials: true },
      );
      if (response.data.success) {
        const extractedData = response.data.data;
        setFormData((prev) => ({
          ...prev,
          reference: extractedData.reference || prev.reference,
          notes: extractedData.notes || prev.notes,
          items: extractedData.items.map((item) => ({
            partname: item.partname,
            quantity: item.quantity,
          })),
        }));
        let msg = `נתונים חולצו בהצלחה! ${extractedData.items.length} פריטים נמצאו.`;
        if (extractedData.notFoundItems?.length > 0)
          msg += ` ${extractedData.notFoundItems.length} מק"טים לא נמצאו.`;
        alert(msg);
      } else {
        setError(response.data.msg || "לא ניתן לחלץ נתונים מה-PDF");
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError("אין הרשאה. אנא התחבר מחדש.");
      } else {
        setError(
          `שגיאה בחילוץ נתונים: ${err.response?.data?.msg || err.message}`,
        );
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get("/nursinghome/sites", {
        withCredentials: true,
      });
      setSites(response.data?.data || []);
    } catch (err) {
      logger.error("Error fetching sites:", err);
    }
  };

  const fetchDeliveryDays = async () => {
    try {
      const response = await axios.get("/nursinghome/delivery-days", {
        withCredentials: true,
      });
      if (response.data?.data) {
        const availableDates = response.data.data;
        setDeliveryDays(availableDates);
        if (availableDates.length > 0) {
          setFormData((prev) => ({
            ...prev,
            dueDate: prev.dueDate || `${availableDates[0].date}T00:00:00+02:00`,
          }));
        }
      }
    } catch (err) {
      logger.error("Error fetching delivery days:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get("/nursinghome/products", {
        withCredentials: true,
      });
      if (response.data?.data) {
        setProducts(response.data.data);
        setIsFreeQuantity(response.data.isFreeQuantity || false);
      } else {
        setError("לא נמצאו מוצרים");
      }
    } catch (err) {
      if (err.response?.status === 403)
        setError("אין הרשאה לצפות במוצרים. אנא התחבר מחדש.");
      else if (err.response?.status === 404)
        setError("לא נמצאו מוצרים זמינים להזמנה.");
      else if (err.response?.status >= 500)
        setError("שגיאת שרת. אנא נסה שוב מאוחר יותר.");
      else
        setError(
          `שגיאה בטעינת המוצרים: ${err.response?.data?.msg || err.message || "נסה שוב מאוחר יותר"}`,
        );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
    }
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { partname: "", quantity: "" }],
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
    setValidationErrors((prev) => {
      const n = { ...prev };
      delete n[`item_${index}_partname`];
      delete n[`item_${index}_quantity`];
      return n;
    });
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === "partname" && value) {
        if (isFreeQuantity) {
          newItems[index].quantity = newItems[index].quantity || 1;
        } else {
          newItems[index].quantity = getPackQuant(value);
        }
      }
      return { ...prev, items: newItems };
    });
    const errorKey = `item_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const n = { ...prev };
        delete n[errorKey];
        return n;
      });
    }
  };

  const FREE_QUANTITY_PARTNAMES = ["202002400004"];
  const handleQuantityBlur = (index) => {
    const item = formData.items[index];
    if (!isFreeQuantity && item.partname && item.quantity && !FREE_QUANTITY_PARTNAMES.includes(item.partname)) {
      const packQuant = getPackQuant(item.partname);
      if (item.quantity > 0 && item.quantity % packQuant !== 0) {
        setQuantityErrorDialog({
          open: true,
          itemIndex: index,
          partname: item.partname,
          enteredQuantity: item.quantity,
          packQuant,
        });
      }
    }
  };

  const getPackQuant = (partname) => {
    const product = products.find((p) => p.PARTNAME === partname);
    return product?.PARTPACK_SUBFORM?.[0]?.PACKQUANT || 1;
  };

  const isValidQuantityMultiple = (partname, quantity) => {
    if (isFreeQuantity || FREE_QUANTITY_PARTNAMES.includes(partname)) return true;
    const packQuant = getPackQuant(partname);
    if (!quantity || quantity <= 0) return false;
    return quantity % packQuant === 0;
  };

  const validateForm = () => {
    const errors = {};
    if (formData.items.length === 0) errors.items = "יש להוסיף לפחות פריט אחד";
    const seenProducts = new Set();
    formData.items.forEach((item, index) => {
      if (!item.partname) {
        errors[`item_${index}_partname`] = "יש לבחור מוצר";
      } else if (seenProducts.has(item.partname)) {
        errors[`item_${index}_partname`] = "מוצר כבר קיים בהזמנה";
      } else {
        seenProducts.add(item.partname);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = "כמות חייבת להיות גדולה מ-0";
      } else if (!isValidQuantityMultiple(item.partname, item.quantity)) {
        errors[`item_${index}_quantity`] =
          `הכמות חייבת להיות כפולה של ${getPackQuant(item.partname)}`;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setError("יש לתקן את השגיאות בטופס");
      return;
    }
    navigate("/nursinghome/confirm", {
      state: { formData, contactName, phoneNum, products, deliveryDays, sites },
    });
  };

  const handleClearForm = () => {
    setFormData({
      reference: "",
      dueDate:
        deliveryDays.length > 0 ? `${deliveryDays[0].date}T00:00:00+02:00` : "",
      dcode: "",
      notes: "",
      driverText: "",
      items: [],
      selectedFiles: [],
    });
    setValidationErrors({});
    setError(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error && products.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={fetchProducts}
          fullWidth
        >
          נסה שוב
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10, px: 2, pt: 1 }}>
      {/* Header */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, mb: 1 }}
      >
        <ShoppingCartIcon sx={{ color: "#1976d2", fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          הזמנה חדשה - בית אבות
        </Typography>
      </Box>

      {/* PDF Upload */}
      <Button
        fullWidth
        variant="contained"
        component="label"
        startIcon={<AttachFileIcon />}
        disabled={uploadingFiles}
        sx={{ mb: 2 }}
      >
        {uploadingFiles ? "מעבד PDF..." : "העלה PDF להזמנה אוטומטית"}
        <input
          type="file"
          hidden
          accept="application/pdf"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (file && file.type === "application/pdf") {
              try {
                const fileData = await readFileInChunks(file);
                setFormData((prev) => ({
                  ...prev,
                  selectedFiles: [...prev.selectedFiles, fileData],
                }));
                await handleExtractFromPdf(0, fileData);
              } catch (err) {
                setError("שגיאה בעיבוד קובץ ה-PDF");
              }
            }
            event.target.value = null;
          }}
        />
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Contact Details */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              פרטי איש קשר
            </Typography>
            <TextField
              label="איש קשר"
              value={contactName}
              disabled
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="נייד"
              value={phoneNum}
              disabled
              fullWidth
              size="small"
            />
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              פרטי הזמנה
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                label="מס' הזמנת רכש"
                value={formData.reference}
                onChange={(e) => handleInputChange("reference", e.target.value)}
                error={!!validationErrors.reference}
                helperText={validationErrors.reference}
                fullWidth
                size="small"
              />

              {sites.length > 1 && (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!validationErrors.dcode}
                >
                  <InputLabel>אתר אספקה</InputLabel>
                  <Select
                    value={formData.dcode}
                    label="אתר אספקה"
                    onChange={(e) => handleInputChange("dcode", e.target.value)}
                  >
                    <MenuItem value="">--</MenuItem>
                    {sites.map((site) => (
                      <MenuItem
                        key={site.CODE}
                        value={site.CODE}
                        sx={{ direction: "ltr" }}
                      >
                        {site.CODEDES}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.dcode && (
                    <FormHelperText>{validationErrors.dcode}</FormHelperText>
                  )}
                </FormControl>
              )}

              <FormControl fullWidth size="small">
                <InputLabel>צפי ליום הפצה *</InputLabel>
                <Select
                  value={formData.dueDate}
                  label="צפי ליום הפצה *"
                  onChange={(e) => handleInputChange("dueDate", e.target.value)}
                  disabled={deliveryDays.length === 0}
                >
                  {deliveryDays.length === 0 ? (
                    <MenuItem value="">טוען ימי חלוקה...</MenuItem>
                  ) : (
                    generateDeliveryOptions().map((option, index) => (
                      <MenuItem
                        key={`${option.date}-${index}`}
                        value={option.value}
                        sx={{ direction: "ltr" }}
                      >
                        {option.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <TextField
                label="הערות"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                multiline
                rows={2}
                fullWidth
                size="small"
              />

              <TextField
                label="הוראות לנהג"
                value={formData.driverText}
                onChange={(e) =>
                  handleInputChange("driverText", e.target.value)
                }
                multiline
                rows={2}
                fullWidth
                size="small"
              />

              {/* File Attach */}
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                disabled={uploadingFiles}
                fullWidth
                size="small"
              >
                {uploadingFiles ? "מעלה קבצים..." : "צרף קבצים"}
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileChange}
                />
              </Button>

              {formData.selectedFiles.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {formData.selectedFiles.map((file, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", gap: 1, alignItems: "center" }}
                    >
                      <Chip
                        label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                        onDelete={() => handleRemoveFile(index)}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                      {file.type === "application/pdf" && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleExtractFromPdf(index)}
                          disabled={uploadingFiles}
                        >
                          חלץ
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                פריטים להזמנה
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                size="small"
              >
                הוסף
              </Button>
            </Box>

            {formData.items.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                לקוח יקר – שים לב את הכמויות יש להזין ביחידות (מלבד הפריט אבאונד)
              </Alert>
            )}

            {validationErrors.items && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {validationErrors.items}
              </Alert>
            )}

            {formData.items.length === 0 && (
              <Alert severity="info">לחץ על "הוסף" להוספת פריטים.</Alert>
            )}

            {formData.items.map((item, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  borderColor:
                    validationErrors[`item_${index}_partname`] ||
                    validationErrors[`item_${index}_quantity`]
                      ? "error.main"
                      : "divider",
                }}
              >
                <CardContent sx={{ pb: "12px !important", pt: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      פריט {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Autocomplete
                    key={advanceConfirm.getAutocompleteKey(index)}
                    options={products}
                    freeSolo
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.PARTNAME} - ${option.PARTDES}`;
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter((option) => {
                        const partname = option.PARTNAME?.toLowerCase() || "";
                        const barcode = option.BARCODE?.toLowerCase() || "";
                        const yarpapart =
                          option.NEO_YARPAPART?.toLowerCase() || "";
                        const partdes = option.PARTDES?.toLowerCase() || "";
                        const custPartNames =
                          option.NEO_CUSTPART_SUBFORM?.map(
                            (cp) => cp.CUSTPARTNAME?.toLowerCase() || "",
                          ) || [];
                        return (
                          partname.includes(searchTerm) ||
                          barcode.includes(searchTerm) ||
                          yarpapart.includes(searchTerm) ||
                          partdes.includes(searchTerm) ||
                          custPartNames.some((cpn) => cpn.includes(searchTerm))
                        );
                      });
                    }}
                    value={
                      products.find((p) => p.PARTNAME === item.partname) ||
                      item.partname ||
                      null
                    }
                    onChange={(event, newValue) => {
                      const partname =
                        typeof newValue === "string"
                          ? newValue
                          : newValue?.PARTNAME || "";

                      if (advanceConfirm.interceptSelection(index, partname)) {
                        return;
                      }

                      handleItemChange(index, "partname", partname);
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input")
                        handleItemChange(index, "partname", newInputValue);
                    }}
                    renderOption={(props, option) => {
                      const packQuant =
                        option.PARTPACK_SUBFORM?.[0]?.PACKQUANT || null;
                      const custPartName =
                        option.NEO_CUSTPART_SUBFORM?.[0]?.CUSTPARTNAME || null;
                      return (
                        <li {...props} key={option.PARTNAME}>
                          <Box sx={{ width: "100%" }}>
                            <Typography variant="body2">
                              <strong>{option.PARTNAME}</strong>
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ mt: 0.5 }}
                            >
                              {renderHighlightedPartDes(option.PARTNAME, option.PARTDES)}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              {option.BARCODE && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  ברקוד: {option.BARCODE}
                                </Typography>
                              )}
                              {custPartName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  מק"ט ספק: {custPartName}
                                </Typography>
                              )}
                              {packQuant && (
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  sx={{ fontWeight: 600 }}
                                >
                                  מגש שלם: {packQuant} יח
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="בחר מוצר *"
                        size="small"
                        error={!!validationErrors[`item_${index}_partname`]}
                        helperText={
                          validationErrors[`item_${index}_partname`] ||
                          'חיפוש לפי מק"ט, שם, ברקוד'
                        }
                        fullWidth
                        sx={{ mb: 1.5 }}
                      />
                    )}
                    fullWidth
                  />

                  {(() => {
                    const selectedProduct = products.find(
                      (p) => p.PARTNAME === item.partname,
                    );
                    return (
                      selectedProduct && (
                        <Typography
                          variant="body2"
                          dir="ltr"
                          sx={{
                            mb: 1.5,
                            textAlign: "right",
                            wordBreak: "break-word",
                            unicodeBidi: "isolate",
                          }}
                        >
                          {selectedProduct.PARTNAME} - {renderHighlightedPartDes(selectedProduct.PARTNAME, selectedProduct.PARTDES)}
                        </Typography>
                      )
                    );
                  })()}

                  <TextField
                    type="number"
                    label="כמות *"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "quantity",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    onBlur={() => handleQuantityBlur(index)}
                    error={!!validationErrors[`item_${index}_quantity`]}
                    helperText={
                      validationErrors[`item_${index}_quantity`] ||
                      "כמות מוצרים"
                    }
                    inputProps={{ min: 1 }}
                    fullWidth
                    size="small"
                  />
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </form>

      {/* Sticky Action Bar */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
          px: 2,
          py: 1.5,
          display: "flex",
          gap: 1.5,
          zIndex: 1000,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Button variant="outlined" onClick={handleClearForm} fullWidth>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={formData.items.length === 0}
          fullWidth
        >
          הצג הזמנה
        </Button>
      </Box>

      {/* Quantity Error Dialog */}
      <Dialog
        open={quantityErrorDialog.open}
        onClose={() =>
          setQuantityErrorDialog({ ...quantityErrorDialog, open: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ pb: 1, backgroundColor: "#fff3e0", color: "#e65100" }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, direction: "ltr" }}>
            שים לב - הכמות לא תואמת למגש שלם
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ direction: "ltr", pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1">
              הכמות שהזנת ({quantityErrorDialog.enteredQuantity}) אינה כפולה של
              הכמות המינימלית.
            </Typography>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>כמות במגש שלם:</strong> {quantityErrorDialog.packQuant}{" "}
                יחידות
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ direction: "ltr", justifyContent: "flex-start", px: 3, pb: 2 }}
        >
          <Button
            onClick={() => {
              const { itemIndex, packQuant, enteredQuantity } =
                quantityErrorDialog;
              const nearestMultiple =
                Math.round(enteredQuantity / packQuant) * packQuant;
              handleItemChange(
                itemIndex,
                "quantity",
                nearestMultiple > 0 ? nearestMultiple : packQuant,
              );
              setQuantityErrorDialog({ ...quantityErrorDialog, open: false });
            }}
            variant="contained"
            color="primary"
            fullWidth
          >
            תקן אוטומטית
          </Button>
          <Button
            onClick={() =>
              setQuantityErrorDialog({ ...quantityErrorDialog, open: false })
            }
            variant="outlined"
            fullWidth
          >
            תקן ידנית
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advance Product Confirmation Dialog */}
      <AdvanceConfirmDialog {...advanceConfirm.dialogProps} />
    </Box>
  );
};

export default NursingHomeOrderFormMobile;
